/**
 * Service for tracking form widget interaction events
 *
 * API Endpoint: POST /cms/widget/events
 * @see https://github.com/XappMedia/chat-widget/blob/master/docs/widget-events-api.md
 */

import { StentorApi } from "../api/stentor-api";
import {
    WidgetEventMetadata,
    WidgetEvent,
    FormOpenEventData,
    EngagedEventData,
    NextStepEventData,
    PreviousStepEventData,
    StepChangeEventData,
    SubmitEventData,
    SubmitFailureEventData,
    CloseEventData,
    PartialDataEventData,
    HandoffRenderedEventData,
    HandoffNoRenderEventData,
    HandoffScheduledEventData,
    HandoffErrorEventData,
} from "../types/events";
import { log, err } from "../utils";
import { getStoredTrackingParameters } from "../utils/tracking";

const XAPP_USER_ID = "__xapp_form_user_id";

/**
 * Service for tracking form widget interaction events.
 * This service provides methods to track user interactions with the form widget
 * without blocking the user experience (all tracking is async and errors are logged).
 *
 * Events are sent to POST /cms/widget/events and stored in OpenSearch as:
 * - form_open -> eventName: "WIDGET_FORM_OPEN"
 * - engaged -> eventName: "WIDGET_FORM_ENGAGED"
 * - next_step -> eventName: "WIDGET_FORM_STEP"
 * - previous_step -> eventName: "WIDGET_FORM_PREV_STEP"
 * - step_change -> eventName: "WIDGET_FORM_STEP_CHANGE"
 * - submit -> eventName: "WIDGET_FORM_SUBMIT"
 * - close -> eventName: "WIDGET_FORM_CLOSE"
 * - partial_data -> eventName: "WIDGET_FORM_ABANDON"
 * - handoff_rendered -> eventName: "WIDGET_FORM_HANDOFF_RENDERED"
 * - handoff_no_render -> eventName: "WIDGET_FORM_HANDOFF_NO_RENDER"
 * - handoff_scheduled -> eventName: "WIDGET_FORM_HANDOFF_SCHEDULED"
 * - handoff_error -> eventName: "WIDGET_FORM_HANDOFF_ERROR"
 */
/**
 * Input that counts as a visitor touching the page. Deliberately broad and document-level: the
 * point is to tell a person from a scripted browser, and a script that drives the form through the
 * DOM produces none of these - as long as only real gestures count, which is why the listener
 * below checks isTrusted. Browsers set that flag only on genuine user input; anything a script
 * dispatches is untrusted and cannot set it.
 */
const INTERACTION_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"];

export class EventTrackingService {
    private api: StentorApi;
    private sessionId: string;
    private formId: string;
    /** When the form opened, so every later event can report how long the visit has lasted. */
    private openedAt?: number;
    /** When the visitor first touched anything. Stays undefined when nobody ever did. */
    private firstInteractionAt?: number;
    private stopWatchingForInteraction?: () => void;

    constructor(api: StentorApi, sessionId: string, formId: string) {
        this.api = api;
        this.sessionId = sessionId;
        this.formId = formId;
    }

    /**
     * Track a form_open event when the form widget is first displayed
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_OPEN"
     */
    public async trackFormOpen(data: FormOpenEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata({ isOpen: true });
            this.watchForFirstInteraction();
            const event: WidgetEvent = {
                eventType: "form_open",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] form_open event tracked: ${data.formName}`);
        } catch (error) {
            err(`[EventTracking] Failed to track form_open event: ${error}`);
            // Don't throw - we don't want to block the user flow
        }
    }

    /**
     * Track an engaged event the first time a user takes a genuine action on
     * the form (manual open, chip click, or first step transition).
     * Additive to form_open - does not replace it. Callers are responsible
     * for only calling this once per session.
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_ENGAGED"
     */
    public async trackEngaged(data: EngagedEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "engaged",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] engaged event tracked: ${data.formName} (${data.trigger})`);
        } catch (error) {
            err(`[EventTracking] Failed to track engaged event: ${error}`);
            // Don't throw - we don't want to block the user flow
        }
    }

    /**
     * Track a next_step event when user navigates to the next form step
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_STEP"
     */
    public async trackNextStep(data: NextStepEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "next_step",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] next_step event tracked: ${data.completedStepName}`);
        } catch (error) {
            err(`[EventTracking] Failed to track next_step event: ${error}`);
            // Don't throw - we don't want to block the user flow
        }
    }

    /**
     * Track a previous_step event when user navigates back to a previous step
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_PREV_STEP"
     */
    public async trackPreviousStep(data: PreviousStepEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "previous_step",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] previous_step event tracked: ${data.fromStepName} -> ${data.toStepName}`);
        } catch (error) {
            err(`[EventTracking] Failed to track previous_step event: ${error}`);
            // Don't throw - we don't want to block the user flow
        }
    }

    /**
     * Track a step_change event for any step navigation (forward or backward)
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_STEP_CHANGE"
     */
    public async trackStepChange(data: StepChangeEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "step_change",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] step_change event tracked: ${data.fromStepName} -> ${data.toStepName} (${data.direction})`);
        } catch (error) {
            err(`[EventTracking] Failed to track step_change event: ${error}`);
            // Don't throw - we don't want to block the user flow
        }
    }

    /**
     * Track a submit event when user submits the final form
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_SUBMIT"
     */
    public async trackSubmit(data: SubmitEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "submit",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] submit event tracked: ${data.finalStepName}`);
        } catch (error) {
            err(`[EventTracking] Failed to track submit event: ${error}`);
            // Don't throw - we don't want to block the user flow
        }
    }

    /**
     * Track a submit that failed to reach the server, after retries.
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_SUBMIT_FAILURE"
     *
     * trackSubmit above fires on this endpoint *before* the FORM_SUBMIT dispatch that
     * actually creates the lead, so a submit event alone never proved a lead existed.
     * This is its counter-signal: submit events minus submit_failure events is the
     * number of submissions that were genuinely dispatched (issue #1373).
     */
    public async trackSubmitFailure(data: SubmitFailureEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "submit_failure",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] submit_failure event tracked: ${data.finalStepName}`);
        } catch (error) {
            err(`[EventTracking] Failed to track submit_failure event: ${error}`);
            // Don't throw - the user is already being shown the failure.
        }
    }

    /**
     * Track a close event when user explicitly closes the form widget
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_CLOSE"
     */
    public async trackClose(data: CloseEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "close",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] close event tracked: ${data.lastStepName}`);
        } catch (error) {
            err(`[EventTracking] Failed to track close event: ${error}`);
            // Don't throw - we don't want to block the user flow
        }
    }

    /**
     * Track a partial_data event when user abandons form (tab close or timeout)
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_ABANDON"
     *
     * @param data - Event data including abandonmentReason ("close_tab" | "timeout")
     */
    public async trackPartialData(data: PartialDataEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "partial_data",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] partial_data event tracked: ${data.lastStepName} (${data.abandonmentReason})`);
        } catch (error) {
            err(`[EventTracking] Failed to track partial_data event: ${error}`);
            // Don't throw - we don't want to block the user flow
        }
    }

    /**
     * Track a handoff_rendered event when a booking-widget handoff step's anchor
     * gains content within its render timeout.
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_HANDOFF_RENDERED"
     */
    public async trackHandoffRendered(data: HandoffRenderedEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "handoff_rendered",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] handoff_rendered event tracked: ${data.stepName} (${data.provider})`);
        } catch (error) {
            err(`[EventTracking] Failed to track handoff_rendered event: ${error}`);
            // Don't throw - we don't want to block the user flow
        }
    }

    /**
     * Track a handoff_no_render event when a booking-widget handoff step's render
     * timeout elapses with an empty anchor.
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_HANDOFF_NO_RENDER"
     */
    public async trackHandoffNoRender(data: HandoffNoRenderEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "handoff_no_render",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] handoff_no_render event tracked: ${data.stepName} (${data.provider})`);
        } catch (error) {
            err(`[EventTracking] Failed to track handoff_no_render event: ${error}`);
            // Don't throw - we don't want to block the user flow
        }
    }

    /**
     * Track a handoff_scheduled event when the partner script invokes the
     * configured success callback. This is how the booking outcome reaches
     * stentor_events -- joinable on sessionId with the lead.
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_HANDOFF_SCHEDULED"
     */
    public async trackHandoffScheduled(data: HandoffScheduledEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "handoff_scheduled",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] handoff_scheduled event tracked: ${data.stepName} (${data.provider})`);
        } catch (error) {
            err(`[EventTracking] Failed to track handoff_scheduled event: ${error}`);
            // Don't throw - we don't want to block the user flow
        }
    }

    /**
     * Track a handoff_error event when a booking-widget handoff step's partner
     * script fails to load, or its scriptSrc is rejected.
     * Stored as: eventType: "AnalyticsEvent", eventName: "WIDGET_FORM_HANDOFF_ERROR"
     */
    public async trackHandoffError(data: HandoffErrorEventData): Promise<void> {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "handoff_error",
                metadata,
                data,
            };

            await this.sendEvent(event);
            log(`[EventTracking] handoff_error event tracked: ${data.stepName} (${data.provider})`);
        } catch (error) {
            err(`[EventTracking] Failed to track handoff_error event: ${error}`);
            // Don't throw - we don't want to block the user flow
        }
    }

    /**
     * Track a partial_data event using beacon/keepalive for page unload scenarios.
     * Use this when the page is being unloaded and you need reliable delivery.
     *
     * @param data - Event data including abandonmentReason ("close_tab" | "timeout")
     * @returns true if the beacon was queued, false otherwise
     */
    public trackPartialDataBeacon(data: PartialDataEventData): boolean {
        try {
            const metadata = this.buildMetadata();
            const event: WidgetEvent = {
                eventType: "partial_data",
                metadata,
                data,
            };

            return this.api.trackEventBeacon(event);
        } catch (error) {
            err(`[EventTracking] Failed to queue partial_data beacon: ${error}`);
            return false;
        }
    }

    /**
     * Build event metadata from current context.
     *
     * Each optional enrichment (userId, currentUrl, UTM/click-id params) is isolated in its
     * own try/catch. localStorage access in particular can throw synchronously - not just
     * return null - in Safari ITP, a sandboxed iframe without allow-same-origin, or under an
     * enterprise storage policy. Without isolation, one such throw used to take down metadata
     * building entirely, silently dropping the event (including userAgent/currentUrl, which
     * don't depend on storage) via the try/catch in trackFormOpen et al. See issue #1504.
     */
    private buildMetadata({ isOpen = false } = {}): WidgetEventMetadata {
        const now = new Date();
        const nowMs = Date.now();
        if (isOpen && this.openedAt === undefined) {
            this.openedAt = nowMs;
        }
        const metadata: WidgetEventMetadata = {
            timestamp: now.toISOString(),
            sessionId: this.sessionId || 'unknown',
            formId: this.formId || 'unknown',
            userAgent: navigator.userAgent,
        };

        // How long the visit has lasted, and how long it took the visitor to touch anything.
        // Both are omitted rather than zeroed when unknown: a 0 would read as a real measurement
        // of an instant bounce, which is exactly what these are used to judge.
        if (this.openedAt !== undefined) {
            metadata.msOnPage = String(Math.max(0, nowMs - this.openedAt));
            if (this.firstInteractionAt !== undefined) {
                metadata.msToFirstInteraction = String(Math.max(0, this.firstInteractionAt - this.openedAt));
            }
        }

        // Environment. Isolated like every other enrichment below - a browser that throws here
        // must still produce an event, not a silent drop (issue #1504).
        try {
            metadata.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
            // Intl unavailable or throwing - skip timezone
        }

        try {
            metadata.language = navigator.language;
            // Screen against viewport: a real browser puts its own chrome between the two, so a
            // viewport exactly the size of the screen is a headless default.
            metadata.screenWidth = String(window.screen.width);
            metadata.screenHeight = String(window.screen.height);
            metadata.viewportWidth = String(window.innerWidth);
            metadata.viewportHeight = String(window.innerHeight);
        } catch (e) {
            // Screen/navigator unavailable (sandboxed frame) - skip the environment fields
        }

        // Add user ID if available (optional per API spec)
        try {
            const userId = this.getUserId();
            if (userId && userId !== 'unknown') {
                metadata.userId = userId;
            }
        } catch (e) {
            // localStorage unavailable (private browsing, sandboxed frame, storage policy) - skip userId
        }

        // Add current URL (exclude localhost for privacy)
        try {
            const href = new URL(window.location.href);
            if (!href?.host.toLowerCase().startsWith('localhost')) {
                metadata.currentUrl = window.location.href;
            }
        } catch (e) {
            // If URL parsing fails, skip currentUrl
        }

        // Add tracking parameters from localStorage
        try {
            const trackingParams = this.getTrackingParameters();
            Object.assign(metadata, trackingParams);
        } catch (e) {
            // localStorage unavailable - skip UTM/click-id params
        }

        return metadata;
    }

    /**
     * Releases anything this tracker is holding on the page.
     *
     * The interaction listeners otherwise live until a trusted gesture fires one of them, which
     * may never happen: FormWidget's embedded mode mounts and unmounts inside a host app, and a
     * widget unmounted before anyone touches it would leave four document listeners - and the
     * closures behind them - accumulating on the page.
     */
    public dispose(): void {
        this.stopWatchingForInteraction?.();
    }

    /**
     * Carries a visit's timing onto a replacement tracker.
     *
     * FormWidget builds a new EventTrackingService every time an intentRequest resolves to a FORM
     * display, and that happens again mid-visit on a chip click. form_open fires only once per
     * mounted widget, so the replacement never calls trackFormOpen and would never learn when the
     * visit began - every later event would silently report no dwell and no interaction, for
     * exactly the multi-step flow these fields exist to measure.
     *
     * The previous tracker's listener is torn down and a fresh one armed here, so a gesture that
     * arrives after the handover still lands on the tracker actually sending events.
     */
    public adoptVisitFrom(previous?: EventTrackingService | null): void {
        if (!previous || previous === this || previous.openedAt === undefined) {
            return;
        }

        this.openedAt = previous.openedAt;
        this.firstInteractionAt = previous.firstInteractionAt;

        previous.stopWatchingForInteraction?.();
        if (this.firstInteractionAt === undefined) {
            this.watchForFirstInteraction();
        }
    }

    /**
     * Records when the visitor first touches the page, then stops listening.
     *
     * Listeners are passive and one-shot, so this costs nothing after the first input and cannot
     * interfere with scrolling. Absence is the signal that matters: a scripted browser drives the
     * form through the DOM and never fires any of these.
     */
    private watchForFirstInteraction(): void {
        if (this.stopWatchingForInteraction || this.firstInteractionAt !== undefined) {
            return;
        }
        if (typeof document === 'undefined') {
            return;
        }

        try {
            const onInteraction = (event: Event): void => {
                // A script can dispatch pointerdown as easily as we can listen for it, so an
                // untrusted event proves nothing. Ignored without tearing the listener down, or
                // one synthetic event would also destroy the real signal that follows it.
                if (!event.isTrusted) {
                    return;
                }
                if (this.firstInteractionAt === undefined) {
                    this.firstInteractionAt = Date.now();
                }
                this.stopWatchingForInteraction?.();
            };

            this.stopWatchingForInteraction = (): void => {
                for (const name of INTERACTION_EVENTS) {
                    document.removeEventListener(name, onInteraction, true);
                }
                this.stopWatchingForInteraction = undefined;
            };

            for (const name of INTERACTION_EVENTS) {
                document.addEventListener(name, onInteraction, { capture: true, passive: true });
            }
        } catch (e) {
            // Listener registration unavailable - the fields simply stay absent
        }
    }

    /**
     * Get user ID from localStorage
     */
    private getUserId(): string | undefined {
        const userId = localStorage.getItem(XAPP_USER_ID);
        return userId || undefined;
    }

    /**
     * Get this form's tracking parameters from localStorage
     */
    private getTrackingParameters(): Record<string, string> {
        return getStoredTrackingParameters(this.api.trackingScope) as Record<string, string>;
    }

    /**
     * Send event to backend API
     */
    private async sendEvent(event: WidgetEvent): Promise<void> {
        try {
            await this.api.trackEvent(event.eventType, event);
        } catch (error) {
            // Log error but don't throw - we don't want to block user flow
            err(`[EventTracking] API call failed: ${error}`);
        }
    }
}
